import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import { useDeleteFee, useFees } from '@/data/fee';

const formSchema = z.object({
  is_confirmed: z.boolean().refine((val) => val === true, {
    message: 'You must confirm this action by checking the box.'
  })
});

const DeleteFeeDialog = ({ fee, setOpen }) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const deleteFee = useDeleteFee();
  const fees = useFees();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      is_confirmed: false
    }
  });

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      setApiError('');
      await deleteFee(fee.id);
      await fees.mutate(
        (currentFees) => currentFees.filter((f) => f.id !== fee.id),
        false
      );
      setSubmitting(false);
      setOpen(false);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={setOpen}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Delete Fee {fee.label}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="is_confirmed"
              render={({ field }) => (
                <FormItem className="">
                  <div className="flex items-center gap-2 space-y-0 ">
                    <FormControl className="flex items-center ">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>I want to delete this fee</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting} variant="destructive">
                Delete Fee
              </SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteFeeDialog;
